import { Component, EventEmitter, inject, Input, Output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { provideTranslocoScope, TranslocoService } from '@jsverse/transloco';
import { ObjectStoreService } from '../../object-store/object-store.service';
import { ArtifactAttr } from './artifact-attr';
import { CameraCaptureDialog } from './camera-capture.dialog';
import { CameraSupportService } from './camera-support.service';

@Component({
  selector: 'app-artifact-selector',
  standalone: true,
  imports: [FormsModule],
  template: `
    <div class="artifact-selector">
      @if (!isSelectorVisible()) {
        <button type="button" class="base-entity-form-focus-action" (click)="showSelector()">Upload file</button>
        @if (isCameraAvailable()) {
          <button type="button" class="base-entity-form-focus-action" data-testid="artifact-camera-capture" (click)="openCamera()">
            {{ cameraButtonLabel() }}
          </button>
        }
      } @else {
        <div class="artifact-selector__inputs">
          <input type="file" [accept]="accept ?? '*/*'" (change)="onFileSelected($event)" [disabled]="isUploading()" />
          <input type="text" [(ngModel)]="artifactName" placeholder="Artifact name" [disabled]="isUploading()" />
          <input type="text" [(ngModel)]="mimeType" placeholder="MIME type" [disabled]="isUploading()" />
          <button type="button" (click)="uploadSelectedFile()" [disabled]="!canUpload()">Upload</button>
          <button type="button" (click)="cancel()" [disabled]="isUploading()">Cancel</button>
        </div>
      }

      @if (isUploading()) {
        <p>Uploading...</p>
      }
    </div>
  `,
  styleUrls: ['../base-entity-form.css'],
  styles: [
    `
      :host-context(.base-entity-form-field:focus-within) .base-entity-form-focus-action {
        display: revert;
      }
    `,
  ],
  providers: [provideTranslocoScope({ scope: 'base_entity', alias: 'base_entity' })],
})
export class ArtifactSelectorComponent {
  private readonly objectStoreService = inject(ObjectStoreService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translocoService = inject(TranslocoService);
  private readonly dialog = inject(MatDialog);
  readonly isCameraAvailable = inject(CameraSupportService).available;

  @Input() accept?: string;
  @Output() artifactUploaded = new EventEmitter<ArtifactAttr>();

  readonly isSelectorVisible = signal(false);
  readonly isUploading = signal(false);
  artifactName = '';
  mimeType = '';

  private selectedFile: File | null = null;

  showSelector(): void {
    this.isSelectorVisible.set(true);
  }

  cameraButtonLabel(): string {
    return this.translocoService.translate<string>('base_entity.artifact.camera_capture');
  }

  /** A capture lands in the same name / MIME type / Upload step as a picked file, so both are uploaded alike. */
  openCamera(): void {
    this.dialog
      .open<CameraCaptureDialog, void, File | undefined>(CameraCaptureDialog, { width: '720px', maxWidth: '95vw' })
      .afterClosed()
      .subscribe((file) => {
        if (!file) return;
        this.selectFile(file);
        this.isSelectorVisible.set(true);
      });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.item(0);

    if (!file) {
      return;
    }

    const derivedMime = file.type || this.deriveMimeType(file.name);
    if (this.accept === 'image/*' && !derivedMime.startsWith('image/')) {
      this.snackBar.open(this.translocoService.translate<string>('base_entity.artifact.invalid_file_type') || 'Only images are allowed', 'Close', { duration: 5000 });
      input.value = '';
      return;
    }

    this.selectFile(file);
  }

  private selectFile(file: File): void {
    this.selectedFile = file;
    this.artifactName = file.name;
    this.mimeType = file.type || this.deriveMimeType(file.name);
  }

  canUpload(): boolean {
    const isMimeValid = this.accept !== 'image/*' || this.mimeType.startsWith('image/');
    return this.selectedFile !== null && this.artifactName.length > 0 && this.mimeType.length > 0 && isMimeValid && !this.isUploading();
  }

  uploadSelectedFile(): void {
    if (!this.selectedFile || !this.canUpload()) {
      return;
    }

    this.isUploading.set(true);

    this.objectStoreService.uploadObject(this.selectedFile, this.artifactName, this.mimeType).subscribe({
      next: (response) => {
        const artifact: ArtifactAttr = {
          bucket: response.bucketName ?? '',
          objectId: response.objectID,
          name: response.fileName || this.artifactName,
          mimeType: response.mimeType || this.mimeType,
        };

        this.artifactUploaded.emit(artifact);
        this.resetToDisplayMode();
      },
      // A failed upload leaves the selector open with the selection intact, so it is distinguishable from a
      // cancel and can be retried without picking the file again. Closing it silently — as this once did —
      // renders an unreachable object store indistinguishable from an upload nobody asked for.
      error: () => {
        this.isUploading.set(false);
        this.snackBar.open(this.translocoService.translate<string>('base_entity.artifact.upload_failure'), 'Close', { duration: 5000 });
      },
    });
  }

  cancel(): void {
    if (this.isUploading()) {
      return;
    }

    this.resetToDisplayMode();
  }

  private resetToDisplayMode(): void {
    this.selectedFile = null;
    this.artifactName = '';
    this.mimeType = '';
    this.isUploading.set(false);
    this.isSelectorVisible.set(false);
  }

  private deriveMimeType(fileName: string): string {
    const extension = fileName.split('.').pop()?.toLowerCase() ?? '';
    const mimeTypeByExtension: Record<string, string> = {
      bmp: 'image/bmp',
      csv: 'text/csv',
      gif: 'image/gif',
      ico: 'image/x-icon',
      jpeg: 'image/jpeg',
      jpg: 'image/jpeg',
      json: 'application/json',
      mov: 'video/quicktime',
      mp4: 'video/mp4',
      pdf: 'application/pdf',
      png: 'image/png',
      svg: 'image/svg+xml',
      txt: 'text/plain',
      webm: 'video/webm',
      webp: 'image/webp',
      xml: 'application/xml',
      zip: 'application/zip',
    };

    return mimeTypeByExtension[extension] ?? 'application/octet-stream';
  }
}
